import { FIELD_TYPE_CATALOGUE } from '@crm/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileText, History, Loader2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { ApiError, describeError } from '../../lib/api.ts';
import {
  api,
  type CandidateInput,
  type CandidateRecord,
  type FieldDefinitionView,
} from '../../lib/client.ts';
import { arrangeFields, layoutQuery } from '../../lib/custom-fields.ts';
import { uploadCandidateDocument } from '../../lib/documents.ts';
import { usePipeline } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { FieldEditor } from '../fields/editor.tsx';
import { Button } from '../ui/button.tsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { Input, nativeSelectClass } from '../ui/input.tsx';
import { useDocumentViewer } from './document-viewer.tsx';

/**
 * Create / edit a candidate (RELAY-941). Standard fields are a fixed subset; the custom
 * fields are laid out as the candidate Overview is (`arrangeFields` over the organisation's
 * layout; until it loads, by `group` and `position`), edited by their type's editor, and every
 * field not hidden from the role is shown (`read` = shown disabled).
 *
 * A NEW candidate starts in the default Candidate Flow pipeline: Stage is a pick of its stages
 * (the first open one preselected) sent as `stageId`. Editing has no Stage field — the stage is
 * moved on the candidate's Pipelines card, where every pipeline it is in has its bar.
 */
const STANDARD: Array<{ key: keyof CandidateInput; label: string }> = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'currentRole', label: 'Current role' },
  { key: 'currentCompany', label: 'Current company' },
  { key: 'country', label: 'Country' },
  { key: 'expectedHourlyRate', label: 'Expected hourly rate (USD)' },
];

export function CandidateForm({
  candidate,
  definitions,
  role,
  onClose,
  onSaved,
  onHistory,
  onUploaded,
}: {
  candidate: CandidateRecord | null;
  definitions: readonly FieldDefinitionView[];
  role: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  /** Opens the History panel for this candidate (RELAY-942); absent for a new one. */
  onHistory?: (() => void) | undefined;
  /** A resume was uploaded from the form (RELAY-943): the list should refetch. */
  onUploaded?: (() => void) | undefined;
}) {
  const [standard, setStandard] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      STANDARD.map((f) => [
        f.key,
        candidate ? String((candidate[f.key as keyof CandidateRecord] as string | null) ?? '') : '',
      ]),
    ),
  );
  const [custom, setCustom] = useState<Record<string, unknown>>(() => ({
    ...(candidate?.custom ?? {}),
  }));
  const flow = usePipeline('candidate');
  const firstOpen = flow?.stages.find((st) => !st.isTerminal) ?? flow?.stages[0];
  // Null until chosen: the first open stage of the default pipeline once it has loaded.
  const [stagePick, setStageId] = useState<string | null>(null);
  const stageId = stagePick ?? firstOpen?.id ?? null;
  const [error, setError] = useState<string | null>(null);
  // RELAY-947: a duplicate the rules warn about (confirm to continue) or block (cannot continue).
  const [duplicate, setDuplicate] = useState<{
    blocked: boolean;
    message: string;
    matches: Array<{ field: string; candidateId: string | null; name: string | null }>;
  } | null>(null);
  const session = useSession();
  const fileInput = useRef<HTMLInputElement>(null);
  const [resume, setResume] = useState(candidate?.resumeDocument ?? null);
  const viewer = useDocumentViewer(setError);
  const [uploading, setUploading] = useState(false);
  const canUpload = session.can('document.upload');
  const uploadResume = async (file: File) => {
    if (!candidate) return;
    setUploading(true);
    setError(null);
    try {
      const doc = await uploadCandidateDocument(candidate.id, file, 'resume');
      setResume({
        id: doc.id,
        filename: doc.filename,
        contentType: doc.contentType,
        sizeBytes: doc.sizeBytes,
      });
      onUploaded?.();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setUploading(false);
    }
  };
  const editable = definitions.filter((d) => (d.visibility[role] ?? 'edit') !== 'hidden');
  const layout = useQuery({
    queryKey: layoutQuery('candidates'),
    queryFn: () => api.layouts.get('candidates'),
    staleTime: 60_000,
  });
  const groups = useMemo(
    () =>
      arrangeFields(layout.data?.layout ?? { groups: [] }, editable, []).map((g) => ({
        name: g.name,
        fields: g.fields
          .map((f) => editable.find((d) => `custom.${d.key}` === f))
          .filter((d): d is FieldDefinitionView => d !== undefined),
      })),
    [layout.data, editable],
  );

  const save = useMutation({
    mutationFn: async (opts: { allowDuplicate?: boolean } = {}) => {
      const body: CandidateInput = {
        ...Object.fromEntries(Object.entries(standard).map(([k, v]) => [k, v === '' ? null : v])),
        custom: Object.fromEntries(
          editable
            .filter((d) => (d.visibility[role] ?? 'edit') === 'edit')
            .map((d) => [d.key, custom[d.key] ?? null]),
        ),
        ...(candidate || !stageId ? {} : { stageId }),
      };
      return candidate
        ? api.candidates.patch(candidate.id, body, opts)
        : api.candidates.create(body, opts);
    },
    onSuccess: () => void onSaved(),
    onError: (err) => {
      if (
        err instanceof ApiError &&
        (err.code === 'duplicate_warning' || err.code === 'duplicate_blocked')
      ) {
        const details = err.details as
          | { matches?: Array<{ field: string; candidateId: string | null; name: string | null }> }
          | undefined;
        setError(null);
        setDuplicate({
          blocked: err.code === 'duplicate_blocked',
          message: err.message,
          matches: details?.matches ?? [],
        });
        return;
      }
      setError(describeError(err));
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setDuplicate(null);
            save.mutate({});
          }}
          className="flex flex-col gap-4"
          data-testid="candidate-form"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 pr-6">
              <span>
                {candidate
                  ? `Edit ${[candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'candidate'}`
                  : 'New candidate'}
              </span>
              {onHistory && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onHistory}
                  data-testid="open-history"
                >
                  <History /> History
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {STANDARD.map((f) => (
              <label key={f.key} className="flex flex-col gap-1 text-sm">
                <span className="font-medium">{f.label}</span>
                <Input
                  aria-label={f.label}
                  value={standard[f.key] ?? ''}
                  onChange={(e) => setStandard({ ...standard, [f.key]: e.target.value })}
                />
              </label>
            ))}
            {!candidate && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Stage{flow ? ` in ${flow.name}` : ''}</span>
                <select
                  aria-label="Stage"
                  className={nativeSelectClass}
                  value={stageId ?? ''}
                  disabled={!flow}
                  onChange={(e) => setStageId(e.target.value)}
                  data-testid="new-candidate-stage"
                >
                  {!flow && <option value="">Loading…</option>}
                  {flow?.stages.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {candidate && (
            <fieldset className="border-border rounded-xl border p-4" data-testid="resume-section">
              <legend className="px-1 text-sm font-medium">Resume</legend>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {resume ? (
                  <button
                    type="button"
                    className="text-brand inline-flex items-center gap-1 hover:underline"
                    onClick={() => viewer.open(resume)}
                    data-testid="form-resume-open"
                  >
                    <FileText className="size-4" /> {resume.filename}
                  </button>
                ) : candidate.resumeFileLink ? (
                  <button
                    type="button"
                    className="text-brand inline-flex items-center gap-1 hover:underline"
                    onClick={() => viewer.openResumeLink(candidate)}
                    data-testid="form-resume-link"
                  >
                    <FileText className="size-4" />{' '}
                    {candidate.resumeFilename ?? 'Resume (RecruitCRM)'}
                  </button>
                ) : (
                  <span className="text-muted-foreground">No resume yet.</span>
                )}
                {canUpload && (
                  <>
                    <input
                      ref={fileInput}
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,application/pdf"
                      aria-label="Upload resume file"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) void uploadResume(f);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-auto"
                      disabled={uploading}
                      onClick={() => fileInput.current?.click()}
                      data-testid="form-resume-upload"
                    >
                      {uploading ? <Loader2 className="animate-spin" /> : <Upload />}{' '}
                      {resume ? 'Replace' : 'Upload'}
                    </Button>
                  </>
                )}
              </div>
              {viewer.viewer}
            </fieldset>
          )}
          {groups.map((group) => (
            <fieldset key={group.name} className="border-border rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">{group.name}</legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.fields.map((d) => (
                  <div key={d.key} className="flex flex-col gap-1 text-sm">
                    <label htmlFor={`cf-${d.key}`} className="font-medium">
                      {d.label}
                      {d.required && <span className="text-danger"> *</span>}
                    </label>
                    <FieldEditor
                      id={`cf-${d.key}`}
                      label={d.label}
                      type={d.type}
                      options={d.options}
                      value={custom[d.key] ?? d.defaultValue ?? null}
                      disabled={(d.visibility[role] ?? 'edit') === 'read'}
                      onChange={(v) => setCustom({ ...custom, [d.key]: v })}
                    />
                    {d.description && (
                      <span className="text-muted-foreground text-xs">{d.description}</span>
                    )}
                    <span className="sr-only">{FIELD_TYPE_CATALOGUE[d.type].label}</span>
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          {duplicate && (
            <div
              className={`rounded-md border p-3 text-sm ${duplicate.blocked ? 'border-danger/40 bg-danger/5' : 'border-warning/40 bg-warning/5'}`}
              role="alert"
              data-testid="duplicate-notice"
            >
              <p className="font-medium">
                {duplicate.blocked
                  ? 'This candidate already exists.'
                  : 'This candidate may already exist.'}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {duplicate.matches.map((m, i) => (
                  <li key={i}>
                    Same {m.field}:{' '}
                    {m.candidateId ? (
                      <a
                        className="text-brand hover:underline"
                        href={`/candidates/${m.candidateId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {m.name ?? 'open candidate'}
                      </a>
                    ) : (
                      'a candidate you cannot open'
                    )}
                  </li>
                ))}
              </ul>
              {!duplicate.blocked && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => save.mutate({ allowDuplicate: true })}
                  data-testid="save-anyway"
                >
                  {candidate ? 'Save anyway' : 'Create anyway'}
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              // A new candidate needs the default pipeline loaded: it starts at one of its stages.
              disabled={save.isPending || (!candidate && !stageId)}
              data-testid="save-candidate"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
